import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { citysprintApis } from '../../../apis';
import { STRINGS } from '../../../constants';
import useCustomSnackbar from '../../../features/CustomSnackbar/useSnackbar';
import {
  SEND_A_CITYSPRINT_PARCEL,
  SEND_A_CITYSPRINT_PARCEL_COMPLETE_BOOKING,
} from '../../../constants/routes';
import configSlice from '../../../redux/configSlice';
import { countriesSelectors } from '../../../redux/countriesSlice';
import { formatUkPrebookCollectionScheduleLabel } from '../../../utils/dateAndTime';
import { CitysprintFlowStepper } from '../components/CitysprintFlowStepper';
import { CitysprintLoseQuoteConfirmModal } from '../components/CitysprintLoseQuoteConfirmModal';
import { CitysprintReviewBookPanels } from '../components/CitysprintReviewBookPanels';
import { CitysprintReviewBookSidebar } from '../components/CitysprintReviewBookSidebar';
import { CITYSPRINT_CONFIRMATION } from '../constants';
import {
  buildCitysprintThreeDSecureData,
  buildMergedDataFromSlice,
} from '../helpers';
import { useCitysprintBackToParcelGuard } from '../hooks/useCitysprintBackToParcelGuard';
import { useCitysprintWizardExitGuard } from '../hooks/useCitysprintWizardExitGuard';
import { checkoutCitysprintBooking, CitysprintWizardSelectors } from '../redux';

function ReviewBookMain({ data, onBackCompleteBooking }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { showSuccess } = useCustomSnackbar();
  const showSuccessRef = useRef(showSuccess);
  showSuccessRef.current = showSuccess;

  useCitysprintWizardExitGuard();

  const { confirmOpen, requestBackToParcel, handleConfirm, handleCancel } =
    useCitysprintBackToParcelGuard();

  const threeDSecure = useMemo(
    () => buildCitysprintThreeDSecureData(data),
    [data]
  );

  const checkoutAmountString = useMemo(() => {
    const n = Number.parseFloat(data.priceIncl);
    return Number.isFinite(n) ? String(n) : '0';
  }, [data.priceIncl]);

  const getClientToken = useCallback(
    () => citysprintApis.getPaymentClientToken(),
    []
  );

  const handleCheckout = useCallback(
    async payload => {
      const { bookingId, oobCode } = await dispatch(
        checkoutCitysprintBooking({
          paymentNonce: payload.nonce,
          mergedData: data,
        })
      ).unwrap();
      showSuccessRef.current({
        message: CITYSPRINT_CONFIRMATION.BOOKING_CREATED_SUCCESS,
      });
      navigate(`/citysprint/${encodeURIComponent(String(bookingId))}`, {
        replace: true,
        state: { invoiceOobCode: oobCode },
      });
    },
    [data, dispatch, navigate]
  );

  const { terms: termsLink } = useSelector(
    configSlice.selectors.getLinksConfig
  );
  const deliveryCountryName = useSelector(state =>
    countriesSelectors.getCountryName(state, STRINGS.GB)
  );

  const collectionSchedule = useMemo(() => {
    if (data.bookingMode === 'prebook') {
      const hour = data.timeReadyHour ?? data.timeHour;
      const minute = data.timeReadyMinute ?? data.timeMinute;
      return (
        formatUkPrebookCollectionScheduleLabel(
          data.collectionDate,
          hour,
          minute
        ) ?? '—'
      );
    }
    return 'Ready now (as soon as possible)';
  }, [data]);

  const ex = parseFloat(data.priceExcl);
  const inc = parseFloat(data.priceIncl);
  const taxAmount = !Number.isNaN(ex) && !Number.isNaN(inc) ? inc - ex : 0;
  const totalIncVatAmount = !Number.isNaN(inc) ? inc : 0;

  return (
    <Box
      sx={{
        py: { xs: 2 },
        p: { md: 4 },
        background: theme.vars.palette.primary.pageBackground,
        flexGrow: 1,
      }}
    >
      <Typography variant='h1' sx={visuallyHidden}>
        {STRINGS.PAYMENT}
      </Typography>
      <Grid container spacing={0} alignItems='center' justifyContent='center'>
        <Grid
          size={{
            xs: 12,
            md: 10,
            lg: 8,
            xl: 6,
          }}
        >
          <CitysprintFlowStepper
            activeStep={2}
            maxReachableStep={2}
            interceptNavigate={stepKey => {
              if (stepKey === 'parcel') {
                requestBackToParcel();
                return true;
              }
              return false;
            }}
          />
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid
              size={{
                xs: 12,
                md: 8,
              }}
            >
              <CitysprintReviewBookPanels
                data={data}
                onAcceptTerms={setAcceptTerms}
                salesConditionsLink={termsLink || '#'}
                deliveryCountryName={deliveryCountryName}
                collectionSchedule={collectionSchedule}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <CitysprintReviewBookSidebar
                data={data}
                totalIncVatAmount={totalIncVatAmount}
                taxAmount={taxAmount}
                onBack={onBackCompleteBooking}
                getClientToken={getClientToken}
                onCheckout={handleCheckout}
                checkoutAmountString={checkoutAmountString}
                threeDSecure={threeDSecure}
                checkoutDisabled={!acceptTerms}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <CitysprintLoseQuoteConfirmModal
        open={confirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Box>
  );
}

const SendCitysprintParcelReviewBook = () => {
  const navigate = useNavigate();
  const parcelStep = useSelector(CitysprintWizardSelectors.getParcelStepValues);
  const detailsStep = useSelector(
    CitysprintWizardSelectors.getDetailsStepValues
  );

  const data = useMemo(
    () => buildMergedDataFromSlice(parcelStep, detailsStep),
    [parcelStep, detailsStep]
  );
  const hasBooking = Boolean(data.serviceTierId && data.vehicleKey);
  const mayResumeDetails = hasBooking;

  useLayoutEffect(() => {
    if (hasBooking) {
      return;
    }
    if (mayResumeDetails) {
      navigate(SEND_A_CITYSPRINT_PARCEL_COMPLETE_BOOKING, { replace: true });
    } else {
      navigate(SEND_A_CITYSPRINT_PARCEL, { replace: true });
    }
  }, [hasBooking, mayResumeDetails, navigate]);

  const backToCompleteBooking = () => {
    navigate(SEND_A_CITYSPRINT_PARCEL_COMPLETE_BOOKING);
  };

  if (!hasBooking) {
    return null;
  }

  return (
    <ReviewBookMain data={data} onBackCompleteBooking={backToCompleteBooking} />
  );
};

export default SendCitysprintParcelReviewBook;
